import { FormatRegistry, type Static, Type as t } from "@sinclair/typebox";
import { hasInvisibleCharacter } from "../policy/text";
import { boolParam, closed, floatParam, intParam, listParam, literals, nullable } from "../schema-util";

export const VIDEO_MAX_BYTES = 10_737_418_240;
export const VIDEO_CHUNK_BYTES = 16_777_216;
export const VIDEO_CODECS = ["h264", "hevc", "av1", "vp9"] as const;
export const VIDEO_STATUSES = [
  "uploading",
  "queued",
  "processing",
  "published",
  "failed",
  "removed",
] as const;
export const VIDEO_STAGES = [
  "uploading",
  "queued",
  "probing",
  "packaging",
  "storing",
  "published",
  "failed",
] as const;
export const VIDEO_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

// ---- enumerations shared by API, DB, Lambdas and web ------------------------------------------

export const ROLES = ["admin", "user", "guest"] as const;
export type Role = (typeof ROLES)[number];

export const CATEGORIES = [
  "Doujinshi",
  "Manga",
  "Artist CG",
  "Game CG",
  "Western",
  "Non-H",
  "Image Set",
  "Cosplay",
  "Asian Porn",
  "Misc",
  "Private",
] as const;
export type Category = (typeof CATEGORIES)[number];

export const GALLERY_STATES = ["listed", "expunged", "replaced", "removed"] as const;
export type GalleryState = (typeof GALLERY_STATES)[number];

/** none = 0, warn = 1, block = 2 in galleries.policy. */
export const POLICY_LEVELS = ["none", "warn", "block"] as const;
export type PolicyLevel = (typeof POLICY_LEVELS)[number];

/** Levels an administrator can assign. `none` is "no rule"; `block` on a fixed tag is not assignable. */
export const ASSIGNABLE_POLICY_LEVELS = ["warn", "block"] as const;
export type AssignablePolicyLevel = (typeof ASSIGNABLE_POLICY_LEVELS)[number];

export const SYNC_STREAMS = ["ex-head", "ex-expunged", "meta"] as const;
export type SyncStreamName = (typeof SYNC_STREAMS)[number];

/** What kind of answer E-Hentai gave. Anything but `ok` ends the run (no automatic retry). */
export const VERDICTS = ["ok", "blank", "login_required", "banned", "unknown"] as const;
export type Verdict = (typeof VERDICTS)[number];

export const DOWNLOAD_ROUTES = ["hitomi", "archive"] as const;
export type DownloadRoute = (typeof DOWNLOAD_ROUTES)[number];
export const REQUESTED_ROUTES = ["auto", "hitomi", "archive"] as const;
export type RequestedRoute = (typeof REQUESTED_ROUTES)[number];
/** hitomi: images as served ("source"). archive: "resample" (1280px), or "original" when no resample exists. */
export const DOWNLOAD_QUALITIES = ["source", "resample", "original"] as const;
export type DownloadQuality = (typeof DOWNLOAD_QUALITIES)[number];
/** Who executes an Archive job: the Lambda, or the EC2 runner after a pre-purchase handoff. */
export const DOWNLOAD_RUNNERS = ["lambda", "ec2"] as const;
export type DownloadRunner = (typeof DOWNLOAD_RUNNERS)[number];
export const DOWNLOAD_STATUSES = ["queued", "running", "done", "failed"] as const;
export type DownloadStatus = (typeof DOWNLOAD_STATUSES)[number];
export const DOWNLOAD_STAGES = [
  "starting",
  "form",
  "purchase",
  "fetch",
  "extract",
  "upload",
  "finalize",
] as const;
export type DownloadStage = (typeof DOWNLOAD_STAGES)[number];

// ---- scalar schemas -----------------------------------------------------------------------

export const Gid = t.Integer({ minimum: 1, description: "E-Hentai gallery id" });
export const IsoDateTime = t.String({
  pattern: "^\\d{4}-\\d{2}-\\d{2}T\\d{2}:\\d{2}:\\d{2}(\\.\\d+)?(Z|[+-]\\d{2}:\\d{2})$",
  description: "RFC 3339 timestamp",
});
export const Uuid = t.String({
  pattern: "^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$",
  description: "UUID (lower case)",
});
/**
 * Format of the `Tag` schema: no invisible character anywhere (policy/text.ts). TypeBox compiles `pattern`
 * without the `u` flag, so the Unicode classes cannot be written as a pattern; a format is checked by this
 * function instead. A format that is not registered fails validation, so this cannot silently turn off.
 */
export const TAG_FORMAT = "volgo-tag";
FormatRegistry.Set(TAG_FORMAT, (value) => !hasInvisibleCharacter(value));

export const Tag = t.String({
  minLength: 1,
  maxLength: 200,
  format: TAG_FORMAT,
  description: "`namespace:name`, or a bare name; no invisible characters",
});
export const RoleSchema = literals(ROLES);
export const CategorySchema = literals(CATEGORIES);
export const GalleryStateSchema = literals(GALLERY_STATES);
export const PolicyLevelSchema = literals(POLICY_LEVELS);
export const AssignablePolicyLevelSchema = literals(ASSIGNABLE_POLICY_LEVELS);
export const SyncStreamSchema = literals(SYNC_STREAMS);
export const SyncFinishReason = literals([
  "caught_up",
  "end_of_list",
  "request_limit",
  "time_budget",
  "queue_empty",
  "item_failures",
] as const);
export const VerdictSchema = literals(VERDICTS);
export const DownloadRouteSchema = literals(DOWNLOAD_ROUTES);
export const RequestedRouteSchema = literals(REQUESTED_ROUTES);
export const DownloadQualitySchema = literals(DOWNLOAD_QUALITIES);
export const DownloadStatusSchema = literals(DOWNLOAD_STATUSES);
export const DownloadRunnerSchema = literals(DOWNLOAD_RUNNERS);
export const DownloadStageSchema = literals(DOWNLOAD_STAGES);

// ---- request parameter schemas (arrive as strings, handlers get decoded values) --------------

/** Path/query gid: digits on the wire, number in handlers. */
export const GidParam = intParam("^[1-9][0-9]{0,14}$", "gallery id");
/** Numeric id of a database row. */
export const IdParam = intParam("^[1-9][0-9]{0,14}$", "row id");
/** 1..100 */
export const LimitParam = intParam("^([1-9][0-9]?|100)$", "page size, 1..100");
export const BoolParam = boolParam();
export const ListParam = listParam;

export const CursorQuery = t.Object({
  cursor: t.Optional(
    t.String({ minLength: 1, maxLength: 512, description: "opaque; pass back nextCursor as is" }),
  ),
  limit: t.Optional(LimitParam),
});

/** Cursor pagination envelope. `nextCursor` is null on the last page. Closed: no other property may ride along. */
export function pageOf<T extends ReturnType<typeof t.Object> | ReturnType<typeof t.Union>>(item: T) {
  return closed({ items: t.Array(item), nextCursor: nullable(t.String()) });
}

export const Empty = t.Null({ description: "no body (204)" });
export const Ok = closed({ ok: t.Literal(true) });

export type OkType = Static<typeof Ok>;
export { closed, floatParam, literals, nullable };
